import { defineStore } from 'pinia';
import { iProjectCase, iProjectStructure, tProjectCases } from '@/Interfaces/projectStructure';
import { getProjectByUrlParams } from '@/libs/getProject';
import { iStoreCaseConfiguration } from '@/Interfaces/storeCaseConfiguration';
import { useStoreCaseConfiguration } from '@/Store/storeCaseConfiguration';
import { toolChestDefaultConfig } from '@/Constants/toolChestDefaultConfig';
import { casesLib } from '@/Constants/casesLib';
import { getFinalCasePrice } from '@/libs/getFinalPrice';
import { colors } from '@/Constants/colors';

declare global {
    interface Window {
        projectCases: {[key: string]: iStoreCaseConfiguration};
    }
}

export const useStoreProjectStructure = defineStore('storeProjectStructure', {
    state: (): iProjectStructure => ({
        projectId: undefined,
        projectName: 'Мой проект',
        image: '',
        cases: [],
        selectedCaseId: undefined,

    }),
    getters: {
        getCaseById: (state) => {
            return (id: string) => {
                return state.cases.find(item => item.id === id);
            };
        },
        getSelectedCase: (state) => {
            return state.cases.find(item => item.id === state.selectedCaseId);
        },
        checkIsDefaultColorById: (state) => {
            return (id: string) => {
                const caseData = window.projectCases[id] as iStoreCaseConfiguration;
                if (state.selectedCaseId === id) {
                    return useStoreCaseConfiguration().getIsDefaultColorSettings;
                } else if (caseData) {
                    const isColorFrameDefault = caseData.colorFrame === colors[0].hex;
                    const isColorFaceDefault = caseData.colorFace === colors[1].hex;
                    return isColorFrameDefault && isColorFaceDefault;
                }
                else {
                    return !!state.cases.find(item => item.id === id)?.cost;
                }
            };
        },
        checkIsDefaultColor: (state) => {
            let isDefaultColor = true;
            state.cases.forEach((caseMeta)=>{
                if (!useStoreProjectStructure().checkIsDefaultColorById(caseMeta.id)) {
                    isDefaultColor = false;
                }
            });

            return isDefaultColor;
        },
        getCaseDataById: state => {
            return (id: string) => {
                return window.projectCases[id] as iStoreCaseConfiguration;
            };
        },
        getSelectedCaseData: state => {
            if (!state.selectedCaseId) return undefined;
            return window.projectCases[state.selectedCaseId] as iStoreCaseConfiguration;
        },
        getProjectTotalPrice:(state):number => {
            const casesForProject = state.cases;
            if(!casesForProject.some(toolCase=>toolCase.cost)) return 0;

            return casesForProject
                .map(toolCase => {
                    return getFinalCasePrice(toolCase.id) * (useStoreProjectStructure().getCaseById(toolCase.id)?.count ?? 1);
                })
                .reduce((acc, price) => acc + price, 0);
        },
        getCasesData: state => window.projectCases,
    },
    actions: {
        selectCase(id: string) {
            if (!window.projectCases[id]) {
                console.warn('Нет данных шкафа с id ', id);
                return;
            }
            useStoreCaseConfiguration().resetStoreConfiguration();
            useStoreCaseConfiguration().$patch(window.projectCases[id]);
        },
        updateCaseDataById(id: string, data: iStoreCaseConfiguration) {
            window.projectCases[id] = JSON.parse(JSON.stringify(data));
        },
        updateCaseDataSelected() {
            if (!this.selectedCaseId) {
                console.error('нет активного шкафа для сохранения конфигурации');
                return;
            }
            this.updateCaseDataById(this.selectedCaseId, useStoreCaseConfiguration().$state);
        },
        setCasesData(data: {[key: string]: iStoreCaseConfiguration}) {
            // сохраняем конфигурации в window
            window.projectCases = JSON.parse(JSON.stringify(data));
        },
        clear() {
            this.projectId = undefined;
            this.projectName = 'Мой проект';
            this.image = '';
            this.cases = [];
            this.selectedCaseId = undefined;
            window.projectCases = {};
        },
        addNewCase(prototypeId: string) {
            const confDefault = JSON.parse(JSON.stringify(toolChestDefaultConfig));
            confDefault.selectedCaseModel = prototypeId;
            const config = casesLib.find(x => x.key === prototypeId);
            if (!config) {
                console.warn('Не найден прототип шкафа ', prototypeId);
                return;
            }
            confDefault.blenderName = config.blenderName;

            const newCaseId = crypto.randomUUID();
            window.projectCases[newCaseId] = JSON.parse(JSON.stringify(confDefault));

            this.cases.push({
                id: newCaseId,
                name: 'Мой шкаф',
                image: '',
                date: Date().toString(),
                cost: 0,
                count: 1,
            });
            this.selectedCaseId = newCaseId;
            useStoreCaseConfiguration().$patch(JSON.parse(JSON.stringify(confDefault)));
        },
        addCase(caseMetaData: iProjectCase) {
            this.cases.push(caseMetaData);
        },
        deleteCaseById(id: string) {
            this.cases = JSON.parse(JSON.stringify(this.cases
                .filter(item => item.id !== id)));
            delete window.projectCases[id];
        },
        setProjectId(id?: string | undefined) {
            if (id && id !== this.projectId) {
                // TODO вот не тут это должно быть
                getProjectByUrlParams();
            }
            this.projectId = id;
        },
        setProjectName(name: string) {
            this.projectName = name;
        },
        setSelectedCaseId(id?: string | undefined) {
            if (!this.cases.find(item => item.id === id)) return;
            this.selectedCaseId = id;
        },
        setProjectImage(image: string) {
            this.image = image;
        },
        setCaseImageById(caseId: string, image: string) {
            const selectedCase = this.cases
                .find(item => item.id === caseId);
            if (selectedCase) {
                selectedCase.image = image;
            }
        },
        setCaseCountById(caseId: string, count: number) {
            const selectedCase = this.cases
                .find(item => item.id === caseId);
            if (selectedCase) {
                selectedCase.count = count;
            }
        },
        setCaseNameById(caseId: string, name: string) {
            const selectedCase = this.cases
                .find(item => item.id === caseId);
            if (selectedCase) {
                selectedCase.name = name;
            }
        },
        setCaseCostById(caseId: string, cost: number) {
            const selectedCase = this.cases
                .find(item => item.id === caseId);

            if (selectedCase) {
                selectedCase.cost = cost;
            }
        },
        importCases(data: tProjectCases) {
            this.cases = [ ...data ];
        },
    },
});
